import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'
import isEqual from 'lodash-es/isEqual'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../constants/common'
import { FILE_SCOPE_REQUEST } from '../../../../constants/file'
import { SIZE } from '../../../../constants/size'
import { useScreen, useUserData } from '../../../../hooks'
import { getItemsIds } from '../../../../utils/common'
import { isFormMobileActions } from '../../../../utils/screen'
import { Checkbox, CHECKBOX_SIZE } from '../../../Checkbox'
import { Divider } from '../../../Divider'
import { EmailThreadPreviewList } from '../../../Email'
import { Field } from '../../../Field'
import { FileList, FilePreview, useFileUpload } from '../../../File'
import {
  FilesPreviewGallery,
  useFilePreviewGallery,
} from '../../../FilesPreviewGallery'
import {
  FormBody,
  FormFieldsContainer,
  FormInlineNotifications,
  useFormValidationErrorsNotification,
} from '../../../Form'
import {
  FormikField,
  getFormikCheckboxFieldValueProps,
  useFormikFieldValue,
} from '../../../Formik'
import { Content } from '../../../Modal'
import { useWysiwygEditorFullScreenHeight } from '../../../WysiwygEditor'
import { useTaskAttachEmailThreads } from '../../Task.hooks'
import { TaskFormActions } from '../../TaskFormActions'
import { TaskPrioritySelectField } from '../../TaskPrioritySelectField'
import {
  TASK_FORM_EMAILS_THREADS_FIELD_NAME,
  TASK_FORM_FILES_FIELD_NAME,
  TASK_FORM_MARK_AS_DONE_FIELD_NAME,
  TASK_FORM_PRIORITY_FIELD_NAME,
  TASK_FORM_SERVICE_REQUEST_FIELD_NAME,
} from '../TaskForm.constants'
import { getTaskFormConfigurationFieldOptions } from '../TaskForm.utils'

import { ConfigurationField } from './ConfigurationField'
import { CreatedField } from './CreatedField'
import { DeadlineField } from './DeadlineField'
import { DescriptionField } from './DescriptionField'
import { FinishedField } from './FinishedField'
import {
  useManagerFieldFillOnEdit,
  usePhaseFieldLoad,
  usePhaseFieldSuccessLoad,
  useServiceRequestFieldChange,
  useTypeFieldChange,
  useTypeFieldLoad,
  useTypeFieldSuccessLoad,
} from './FormFields.hooks'
import { ManagerField } from './ManagerField'
import { PhaseField } from './PhaseField'
import { ServiceRequestField } from './ServiceRequestField'
import { TitleField } from './TitleField'
import { TypeField } from './TypeField'
import { UpdatedField } from './UpdatedField'
import { VisibilityField } from './VisibilityField'

import * as styles from './FormFields.module.css'

function FormFields({
  ContentComponent = Content,
  ServiceRequestAddon,
  contentComponentProps: {
    size: contentSize = SIZE.X_LARGE,
    ...contentComponentProps
  } = EMPTY_OBJECT,
  hidePhase,
  hideServiceRequest,
  isEditing,
  loadingFiles,
  notifications,
  phases,
  serviceRequestAddonProps,
  setModal,
  submitButtonText,
  onCancel,
  onRemoveNotification,
  onSetInitialValues,
  onSetNotification,
}) {
  const { dirty, initialValues, isSubmitting, values } = useFormikContext()

  const { isMobileScreenSize, width: screenWidth } = useScreen() // TODO make same by css media instead of useScreen

  const { t } = useTranslation()

  const { contentRef, contentWrapperRef, handleEditorInit } =
    useWysiwygEditorFullScreenHeight({
      isFullScreen: contentComponentProps?.isFullScreen,
    })

  const [files, setFiles] = useFormikFieldValue(TASK_FORM_FILES_FIELD_NAME)

  const [emails, setEmails] = useFormikFieldValue(
    TASK_FORM_EMAILS_THREADS_FIELD_NAME
  )

  const {
    filesError,
    filesUploading,
    handleExcludeFile,
    handleRemoveFile,
    handleStartUploading,
    handleUploadErrors,
    handleUploadFile,
  } = useFileUpload({
    files,
    onSetFiles: setFiles,
  })

  const { activeFileIndex, handleClickFile, handleCloseFilePreview } =
    useFilePreviewGallery({ files })

  const currentUser = useUserData()

  const {
    isLoading: isPhaseFieldLoading,
    items: phaseItems,
    setItems: setPhaseItems,
  } = usePhaseFieldLoad({ phases })

  usePhaseFieldSuccessLoad(phaseItems)

  useServiceRequestFieldChange({
    currentUser,
    setEmails,
    setPhaseItems,
  })

  const { isLoading: isTypeFieldLoading, items: typeItems } = useTypeFieldLoad()

  useManagerFieldFillOnEdit({ currentUser, isEditing, onSetInitialValues })

  useTypeFieldChange()
  useTypeFieldSuccessLoad(typeItems, onSetInitialValues)
  // END SIDE EFFECTS BLOCK

  const { handleDeleteEmail, handleOpenEmailThreadsModal } =
    useTaskAttachEmailThreads({
      emails,
      isEditing,
      setEmails,
      setModal,
      values,
      onSetInitialValues,
    })

  useFormValidationErrorsNotification({
    files: { filesError, filesUploading },
    onRemoveNotification,
    onSetNotification,
  })

  function handleDeleteFile(id) {
    if (getItemsIds(files)?.includes(id)) {
      handleExcludeFile(id)
    } else {
      handleRemoveFile(id)
    }
  }

  function renderFile(file, key) {
    return (
      <FilePreview
        disabled={isSubmitting}
        file={file}
        key={key}
        onClick={handleClickFile}
        onRemoveFile={handleDeleteFile}
      />
    )
  }

  function handleCancel() {
    return onCancel({
      dirty,
      files: isEqual(files, initialValues[TASK_FORM_FILES_FIELD_NAME])
        ? EMPTY_LIST
        : files,
    })
  }

  const showFiles =
    loadingFiles ||
    files?.length > 0 ||
    filesUploading?.length > 0 ||
    filesError?.length > 0

  const isEmptyServiceRequest =
    !values[TASK_FORM_SERVICE_REQUEST_FIELD_NAME]?.value

  const isMobileActions = isFormMobileActions({ screenWidth })

  return (
    <FormBody
      Actions={TaskFormActions}
      actionsProps={{
        ButtonAdd: false,
        ButtonClear: false,
        UploadComponent: null,
        cancelProps: {
          onClick: handleCancel,
        },
        className: styles.actions,
        isMobileActions,
        isSubmitting,
        markAsDoneFieldName: TASK_FORM_MARK_AS_DONE_FIELD_NAME,
        submitProps: {
          isLoading: isSubmitting,
          title: submitButtonText,
        },
        uploadProps: {
          onFinishUpload: handleUploadFile,
          onStartUpload: handleStartUploading,
          onUploadErrors: handleUploadErrors,
        },
        onOpenEmailThreadsModal: handleOpenEmailThreadsModal,
      }}
    >
      <ContentComponent
        {...contentComponentProps}
        contentRef={contentRef}
        contentWrapperRef={contentWrapperRef}
        size={contentSize}
      >
        <FormInlineNotifications notifications={notifications} />

        <FormFieldsContainer className={styles.formFields}>
          {!hideServiceRequest ? (
            <ServiceRequestField setModal={setModal} />
          ) : null}
          {ServiceRequestAddon ? (
            <ServiceRequestAddon {...serviceRequestAddonProps} />
          ) : null}
          {!hidePhase ? (
            <PhaseField
              isEmptyServiceRequest={isEmptyServiceRequest}
              isLoading={isPhaseFieldLoading}
              items={phaseItems}
            />
          ) : null}
          <TypeField
            isFullScreen={contentComponentProps.isFullScreen}
            isLoading={isTypeFieldLoading}
            items={typeItems}
          />
          <TitleField wrapperClassName={styles.wrapperClassName} />
          <DescriptionField
            isEmptyServiceRequest={isEmptyServiceRequest}
            onInit={handleEditorInit}
          />
          <ManagerField isEmptyServiceRequest={isEmptyServiceRequest} />
          <ConfigurationField
            getOptions={getTaskFormConfigurationFieldOptions}
          />
          <DeadlineField />
          <TaskPrioritySelectField
            controlProps={{
              borderless: !isMobileScreenSize,
              contentProps: {
                inputProps: {
                  placeholder: t('Task.FormPriorityFieldPlaceholderKey'),
                },
              },
            }}
            fieldComponentProps={{
              label: `${t('Task.FormPriorityFieldLabelKey')}:`,
              shouldAdaptToMobileScreen: true,
            }}
            name={TASK_FORM_PRIORITY_FIELD_NAME}
          />
          <VisibilityField />
          {showFiles ? (
            <Field
              shouldAdaptToMobileScreen
              disabled={isSubmitting}
              isLoading={loadingFiles}
              label={`${t('Task.FormAttachLabelKey')}:`}
            >
              <FileList
                errorItems={filesError}
                files={files}
                renderItem={renderFile}
                uploadingItems={filesUploading}
                onRemoveFile={handleRemoveFile}
              />
            </Field>
          ) : null}
          {emails?.length > 0 ? (
            <Field
              shouldAdaptToMobileScreen
              label={`${t('Task.FormEmailThreadsAttachLabelKey')}:`}
            >
              <EmailThreadPreviewList
                disabled={isSubmitting}
                items={emails}
                onDelete={handleDeleteEmail}
              />
            </Field>
          ) : null}
          {isEditing ? (
            <>
              <Divider className={styles.divider} />
              <CreatedField />
              <UpdatedField />
              <FinishedField />
            </>
          ) : null}
          <FilesPreviewGallery
            files={files}
            initActive={activeFileIndex}
            scope={FILE_SCOPE_REQUEST}
            subject={initialValues[TASK_FORM_SERVICE_REQUEST_FIELD_NAME]?.value}
            onClose={handleCloseFilePreview}
          />
          {isMobileActions ? (
            <FormikField
              Component={Checkbox}
              Field={Fragment}
              className={styles.markAsDoneAction}
              getValueProps={getFormikCheckboxFieldValueProps}
              label={t('Task.FormMarkAsDoneLabelKey')}
              name={TASK_FORM_MARK_AS_DONE_FIELD_NAME}
              size={CHECKBOX_SIZE.small}
            />
          ) : null}
        </FormFieldsContainer>
      </ContentComponent>
    </FormBody>
  )
}

export default FormFields
